import { camelize } from 'casing'
import type { SyncRecord } from '../../oppsync/components/CoSell/types'
import type { ReferralSyncStatus } from '../../oppsync/modules/CoSell/reducer'
import type { SyncObjectType } from '../../oppsync/modules/salesPipeline/syncObject/reducer'
import type { PartnerObjectType } from '../../oppsync/modules/salesPipeline/partnerObjects/reducer'
import { parseResultJsonError } from './CoSellHelpers/helperFunctions'
import type { syncAttemptResultType } from './constants'

type AnyRecord = Record<string, unknown>

const NULL_LIKE_MESSAGES = new Set(['null', '["null"]', '[]', ''])

const parseSyncStatus = (value: unknown): ReferralSyncStatus => {
  const normalized = String(value ?? '').toLowerCase()
  if (normalized === 'success') return 'success'
  if (normalized === 'error' || normalized === 'failed') return 'error'
  if (normalized === 'warning') return 'warning'
  if (normalized === 'in_progress') return 'in_progress'
  return 'in_progress'
}

export const mapSyncDirectionToLegacyDirection = (
  syncDirection: string | undefined | null
): 'ace' | 'crm' => {
  return syncDirection === 'crm_to_ace' ? 'ace' : 'crm'
}

const toSyncAttemptResultType = (value: unknown): syncAttemptResultType => {
  return parseSyncStatus(value) as syncAttemptResultType
}

const formatSyncMessage = (value: unknown): string => {
  if (Array.isArray(value)) {
    const joined = value
      .map(item => String(item ?? ''))
      .filter(item => item && !NULL_LIKE_MESSAGES.has(item))
      .join('; ')
    return joined
  }
  if (typeof value === 'string' && !NULL_LIKE_MESSAGES.has(value)) {
    return value
  }
  return ''
}

const resolveSyncDirection = (sync: AnyRecord): SyncRecord['syncDirection'] => {
  const direct = sync.syncDirection ?? sync.sync_direction
  if (direct === 'crm_to_ace' || direct === 'ace_to_crm') {
    return direct
  }
  const legacyDirection = String(sync.direction ?? '')
  return legacyDirection === 'ace' ? 'crm_to_ace' : 'ace_to_crm'
}

const getSyncTimestamp = (
  sync: AnyRecord,
  field: 'updated' | 'created'
): number => {
  const value =
    field === 'updated'
      ? (sync.updatedAt ?? sync.updated_at)
      : (sync.createdAt ?? sync.created_at)
  return new Date(String(value ?? 0)).getTime() || 0
}

const deriveLastSyncStatusFromRows = (syncRows: AnyRecord[]): string => {
  if (syncRows.length === 0) {
    return ''
  }

  const latestSync = [...syncRows]
    .map(row => camelize(row) as AnyRecord)
    .sort(
      (a, b) => getSyncTimestamp(b, 'updated') - getSyncTimestamp(a, 'updated')
    )[0]

  return parseSyncStatus(
    latestSync.status ??
      latestSync.resultJsonType ??
      latestSync.result_json_type
  )
}

const deriveLastSyncTimestampsFromRows = (
  syncRows: AnyRecord[]
): { createdAt: string; updatedAt: string } => {
  if (syncRows.length === 0) {
    return { createdAt: '', updatedAt: '' }
  }

  const latestSync = [...syncRows]
    .map(row => camelize(row) as AnyRecord)
    .sort(
      (a, b) => getSyncTimestamp(b, 'updated') - getSyncTimestamp(a, 'updated')
    )[0]

  return {
    createdAt: String(latestSync.createdAt ?? latestSync.created_at ?? ''),
    updatedAt: String(latestSync.updatedAt ?? latestSync.updated_at ?? ''),
  }
}

const isTransformedGuidance = (
  value: unknown
): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const payload = value as Record<string, unknown>
  return (
    typeof payload.summary === 'string' ||
    typeof payload.why_this_happens === 'string' ||
    typeof payload.whyThisHappens === 'string' ||
    Array.isArray(payload.steps)
  )
}

const mergeGuidanceItems = (
  items: Record<string, unknown>[]
): Record<string, unknown> | undefined => {
  if (items.length === 0) {
    return undefined
  }
  if (items.length === 1) {
    return items[0]
  }
  const summaries: string[] = []
  const steps: unknown[] = []
  const seenSteps = new Set<string>()
  const escalations: string[] = []
  items.forEach(item => {
    const summary = typeof item.summary === 'string' ? item.summary.trim() : ''
    if (summary && !summaries.includes(summary)) {
      summaries.push(summary)
    }
    if (Array.isArray(item.steps)) {
      item.steps.forEach(step => {
        if (!step || typeof step !== 'object') {
          return
        }
        const stepRecord = step as Record<string, unknown>
        const action =
          typeof stepRecord.action === 'string' ? stepRecord.action.trim() : ''
        const detail =
          typeof stepRecord.detail === 'string' ? stepRecord.detail.trim() : ''
        const text = [action, detail].filter(Boolean).join(' ')
        if (text && !seenSteps.has(text)) {
          seenSteps.add(text)
          steps.push(step)
        }
      })
    }
    const escalation =
      typeof item.escalation === 'string' ? item.escalation.trim() : ''
    if (escalation && !escalations.includes(escalation)) {
      escalations.push(escalation)
    }
  })
  return {
    ...items[0],
    summary: summaries.join(' '),
    why_this_happens: '',
    whyThisHappens: '',
    steps,
    escalation: escalations.join(' '),
  }
}

const parseGuidanceFromUnknown = (
  value: unknown
): Record<string, unknown> | undefined => {
  if (isTransformedGuidance(value)) {
    return value
  }
  if (Array.isArray(value)) {
    return mergeGuidanceItems(value.filter(isTransformedGuidance))
  }
  if (typeof value === 'string' && value.trim() !== '') {
    try {
      return parseGuidanceFromUnknown(JSON.parse(value))
    } catch {
      return undefined
    }
  }
  return undefined
}

const extractSyncGuidance = (
  camelized: AnyRecord
): Record<string, unknown> | undefined => {
  return (
    parseGuidanceFromUnknown(camelized.errorGuidance) ??
    parseGuidanceFromUnknown(camelized.error_guidance) ??
    parseGuidanceFromUnknown(camelized.resultJson) ??
    parseGuidanceFromUnknown(camelized.result_json) ??
    parseGuidanceFromUnknown(camelized.message)
  )
}

const objectRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return undefined
}

const parseAwsResultJson = (
  camelized: AnyRecord
): Record<string, unknown> | undefined => {
  const guidance = extractSyncGuidance(camelized)
  if (guidance) {
    return guidance
  }

  const rawResult =
    objectRecord(camelized.resultJson) ?? objectRecord(camelized.result_json)

  if (!rawResult) {
    return undefined
  }

  const parsed = parseResultJsonError(rawResult)
  return parsed.errors.length > 0
    ? (parsed as unknown as Record<string, unknown>)
    : rawResult
}

export const mapPartnerObjectSyncsToSyncRecords = (
  syncs: AnyRecord[],
  referralId: string
): SyncRecord[] =>
  syncs.map((sync, index) => {
    const camelized = camelize(sync) as AnyRecord
    const status = parseSyncStatus(camelized.status ?? camelized.resultJsonType)
    const parsedResultJson = parseAwsResultJson(camelized)
    const summaryMessage =
      typeof parsedResultJson?.summary === 'string'
        ? parsedResultJson.summary
        : undefined

    return {
      id: String(
        camelized.syncId ?? camelized.sync_id ?? camelized.id ?? `sync-${index}`
      ),
      labraReferralId: referralId,
      status,
      message: formatSyncMessage(
        summaryMessage ??
          camelized.errorMessage ??
          camelized.errorResolution ??
          camelized.message ??
          camelized.resultJson
      ),
      ...(parsedResultJson ? { resultJson: parsedResultJson } : {}),
      syncDirection: resolveSyncDirection(camelized),
      createdAt: String(camelized.createdAt ?? camelized.created_at ?? ''),
      updatedAt: String(camelized.updatedAt ?? camelized.updated_at ?? ''),
    }
  })

export const mapSyncObjectToSyncRecords = (
  syncObject: SyncObjectType | null | undefined,
  referralId?: string
): SyncRecord[] => {
  if (!syncObject || !Array.isArray(syncObject.syncAttempts)) {
    return []
  }
  const targetReferralId = referralId || syncObject.id || ''
  return syncObject.syncAttempts.map((attempt, index) => {
    const attemptRecord = attempt as AnyRecord
    const rawResultJson =
      extractSyncGuidance(attemptRecord) ??
      attempt.resultJsonObject ??
      objectRecord(attemptRecord.result_json)

    return {
      id: String(attemptRecord.id ?? `sync-${index}`),
      labraReferralId: targetReferralId,
      status: parseSyncStatus(attempt.resultJsonType),
      message: attempt.errorMessage || attempt.resultJson || '',
      ...(rawResultJson ? { resultJson: rawResultJson } : {}),
      syncDirection: attempt.direction === 'ace' ? 'crm_to_ace' : 'ace_to_crm',
      createdAt: String(attempt.createdAt ?? ''),
      updatedAt: String(attempt.updatedAt ?? ''),
    }
  })
}

const mapListFields = (camelized: AnyRecord): PartnerObjectType => ({
  id: String(camelized.id ?? camelized.referralId ?? ''),
  crmId: String(camelized.crmObjectId ?? camelized.crmId ?? ''),
  stage: String(camelized.stage ?? ''),
  aceId: String(camelized.cloudObjectId ?? camelized.aceId ?? ''),
  partnerAcceptanceStatus: String(camelized.partnerAcceptanceStatus ?? ''),
  status: String(camelized.awsAcceptanceStatus ?? camelized.status ?? ''),
  lastSyncCreatedAt: String(
    camelized.lastSyncAttemptCreatedAt ?? camelized.lastSyncCreatedAt ?? ''
  ),
  lastSyncUpdatedAt: String(
    camelized.lastSyncAttemptUpdatedAt ?? camelized.lastSyncUpdatedAt ?? ''
  ),
  name: String(camelized.partnerProjectTitle ?? camelized.name ?? ''),
  originatedAt: String(camelized.bornAt ?? camelized.originatedAt ?? ''),
  referredBy: String(camelized.referredBy ?? ''),
  createdDate: String(camelized.createdDate ?? ''),
  resultJsonType: String(
    camelized.lastSyncStatus ?? camelized.resultJsonType ?? ''
  ),
})

export const normalizePartnerObjectListItem = (
  raw: AnyRecord
): PartnerObjectType => mapListFields(camelize(raw) as AnyRecord)

export const normalizePartnerObjectDetail = (
  raw: AnyRecord
): SyncObjectType => {
  const camelized = camelize(raw) as AnyRecord
  const syncRows =
    (camelized.syncs as AnyRecord[] | undefined) ??
    (camelized.syncAttempts as AnyRecord[] | undefined) ??
    []

  const syncAttempts = syncRows.map(row => {
    const sync = camelize(row) as AnyRecord
    const guidance = extractSyncGuidance(sync)
    return {
      direction: mapSyncDirectionToLegacyDirection(
        String(
          sync.syncDirection ?? sync.sync_direction ?? sync.direction ?? ''
        )
      ),
      actionName: (sync.actionName ?? sync.action_name ?? 'update') as
        | 'create'
        | 'update',
      resultJsonType: toSyncAttemptResultType(
        sync.status ?? sync.resultJsonType ?? sync.result_json_type
      ),
      resultJson: formatSyncMessage(
        sync.errorMessage ?? sync.resultJson ?? sync.result_json
      ),
      ...(guidance ? { resultJsonObject: guidance } : {}),
      createdAt: String(sync.createdAt ?? sync.created_at ?? ''),
      updatedAt: String(sync.updatedAt ?? sync.updated_at ?? ''),
      errorSource: sync.errorSource as 'ace' | 'crm' | 'internal' | undefined,
      errorResolution: formatSyncMessage(
        sync.errorMessage ?? sync.errorResolution
      ),
    }
  })

  const listFields = mapListFields(camelized)
  const derivedLastSyncTimestamps = deriveLastSyncTimestampsFromRows(syncRows)
  const resultJsonType =
    listFields.resultJsonType || deriveLastSyncStatusFromRows(syncRows)

  return {
    id: listFields.id,
    objectType: (camelized.objectType ?? 'opportunity') as CRMObjectType,
    crmId: listFields.crmId,
    stage: listFields.stage,
    aceId: listFields.aceId,
    lastSyncInvokedAt:
      listFields.lastSyncCreatedAt || derivedLastSyncTimestamps.createdAt,
    lastSyncCompletedAt:
      listFields.lastSyncUpdatedAt || derivedLastSyncTimestamps.updatedAt,
    name: listFields.name,
    company: String(camelized.company ?? ''),
    originatedAt: listFields.originatedAt,
    referredBy: listFields.referredBy,
    createdAt: String(camelized.createdAt ?? ''),
    resultJsonType,
    syncAttempts,
  }
}

export const extractPartnerObjectSyncs = (raw: AnyRecord): AnyRecord[] => {
  const camelized = camelize(raw) as AnyRecord
  return (
    (camelized.syncs as AnyRecord[] | undefined) ??
    (camelized.syncAttempts as AnyRecord[] | undefined) ??
    []
  )
}
